import React, { useEffect } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate } from "react-router-dom";

/*
 * Member Resources — printable Home Computer Safety Checklist.
 * Members-only. The checklist is print-optimized: Ctrl/Cmd+P produces
 * a clean handout. No personal data is collected here.
 */

const checklist = [
  {
    section: "🛡️ Account Security",
    items: [
      "Use a different password for every important account (email, bank, shopping)",
      "Turn on two-factor authentication (2FA) for email and banking first",
      "Use a password manager instead of a notebook or browser-sticky notes",
      "Never share verification codes with anyone — no real company will ask",
    ],
  },
  {
    section: "🎣 Scam & Phishing Defense",
    items: [
      "Hover over links before clicking — does the address match who claims to send it?",
      "Banks, the IRS, and Apple/Google NEVER ask for passwords or codes by phone or email",
      "Be suspicious of urgency: 'act now', 'your account will be closed', 'grandchild in jail'",
      "If a pop-up says your computer is infected and to call a number — it's a scam. Close it",
    ],
  },
  {
    section: "💻 Computer Health",
    items: [
      "Install updates for your operating system and browser when prompted",
      "Keep one reputable antivirus active (Windows Defender is free and fine)",
      "Restart your computer at least once a week",
      "Back up important photos and documents to the cloud or an external drive monthly",
    ],
  },
  {
    section: "🌐 Home Network",
    items: [
      "Change your Wi-Fi router's admin password from the factory default",
      "Use WPA2 or WPA3 Wi-Fi security (not WEP or open Wi-Fi)",
      "Put smart devices (cameras, TVs, speakers) on a guest network if your router supports it",
    ],
  },
  {
    section: "📞 When Something Goes Wrong",
    items: [
      "Disconnect from the internet if you suspect an infection (unplug the cable / turn off Wi-Fi)",
      "Never pay a ransom or a 'tech support' caller — contact ZelTechnology instead",
      "Log in to your email from a known-clean device to change passwords if compromised",
      "Ask us: members get free help at zeltechnology.org — that's what we're here for",
    ],
  },
];

const Resources = () => {
  useDocTitle("ZelT | Member Resources");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("zelt_token");
    if (!token) {
      navigate("/membership");
    }
  }, [navigate]);

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20 print:py-0">
        <div className="mx-4 max-w-3xl mx-auto">
          <div className="flex justify-between items-center flex-wrap gap-4 mb-8 no-print">
            <h1 className="text-4xl font-extrabold text-blue-900">
              Member Resources
            </h1>
            <button
              onClick={() => window.print()}
              className="px-5 py-2.5 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800 transition"
            >
              🖨️ Print Checklist
            </button>
          </div>

          <div className="mb-8 p-5 rounded-2xl text-white shadow-lg" style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}>
            <p className="font-bold text-lg">The 10+ Point Home Computer Safety Checklist</p>
            <p className="text-white/90 text-sm mt-1">
              Print it, stick it by the monitor, share it with someone you love.
              A new ZelTechnology member benefit.
            </p>
          </div>

          {checklist.map((group) => (
            <div key={group.section} className="mb-6">
              <h2 className="text-xl font-bold text-blue-900 mb-2 border-b-2 border-teal-400 pb-1">
                {group.section}
              </h2>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-gray-700">
                    <span className="mt-1 w-4 h-4 border-2 border-blue-900 rounded flex-shrink-0 print:inline-block" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <p className="text-center text-gray-400 text-sm mt-8">
            © {new Date().getFullYear()} ZelTechnology — a nonprofit. Free to share unchanged.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Resources;
